import { EMPTY_TILE, TILE_FLIP_X, TILE_FLIP_Y, packTile, unpackTile, withoutTileFlips } from "../document/packed-tile.ts";
import { downsampleGrid, upsampleGrid } from "../document/grid.ts";
import { COLLISION_CELLS_PER_TILE, COMPILED_COLLISION_CELLS_PER_TILE, type MapDocument, type PropInstance, type TerrainLayer, type WorldDocument } from "../document/types.ts";
import { DEFAULT_VARIANT } from "../assets/manifest.ts";
import { stateFootprint, thingAnchor } from "./behaviour.ts";
import { COMPILED_MIN_COVER, stampFootprint, stampPassage, type PropFootprint } from "./footprint.ts";
import { blockingFootprint, passageFootprint } from "./prop-footprint.ts";

export { COLLISION_CELLS_PER_TILE, COMPILED_COLLISION_CELLS_PER_TILE, downsampleGrid, upsampleGrid };

/**
 * Compiled cells per authoring cell along each axis, the factor the compile doubles its authoring grid by.
 */
const COMPILED_PER_AUTHORING = COMPILED_COLLISION_CELLS_PER_TILE / COLLISION_CELLS_PER_TILE;
import type { CollisionRule, CompileContext } from "./context.ts";
import type { ResolvedTileLayer } from "./resolve-map.ts";

/**
 * Builds a map's collision grid from game-declared rules (ADR-017): one opaque byte per cell, the bitwise
 * OR of every rule's value over the cells it covers, plus every hand-painted collision layer's bytes
 * (ADR-028). Daedalus does not interpret the bits, with one exception the game declares: a painted byte
 * holding the game's open mask opens its cell, so the rules over terrain write nothing there, and the mask
 * is stripped from what is written (ADR-017's amendment of 2026-10-04). A placed prop whose footprint holds a
 * passage opens the cells under it the same way, where the game declares a mask (the amendment of 2026-10-05).
 */

const MAX_VALUE = 255;

/**
 * Checks a byte value.
 *
 * @param value Candidate.
 * @param what Name for the error.
 * @throws Error when the value is not an integer in 0 to 255.
 */
function assertByte(value: number, what: string): void {
  if (!Number.isInteger(value) || value < 0 || value > MAX_VALUE) {
    throw new Error(`collision ${what} ${value} is not an integer in 0 to ${MAX_VALUE}`);
  }
}

/**
 * Checks that a value the compile writes carries no bit of the game's open mask, which only paint may hold,
 * so a compiled byte is never mistaken for an opening.
 *
 * @param value The value.
 * @param what Name for the error.
 * @param open The open mask.
 * @throws Error when the value carries a bit of the mask.
 */
function assertNotOpen(value: number, what: string, open: number): void {
  if ((value & open) !== 0) {
    throw new Error(`collision ${what} ${value} carries the open mask ${open}, which only paint may hold`);
  }
}

/**
 * The cells a map opens (ADR-017's amendment of 2026-10-04): 1 where any collision layer's byte holds a bit
 * of the game's open mask.
 *
 * @param map Map document; every collision layer must hold `size` cells.
 * @param open The open mask.
 * @param size Cells in the grid.
 * @returns One byte per cell, or null when the game declares no mask or no cell holds it.
 */
function openedCells(map: MapDocument, open: number, size: number): Uint8Array | null {
  let opened: Uint8Array | null = null;
  if (open === 0) {
    return opened;
  }
  for (const layer of map.layers) {
    if (layer.kind !== "collision") {
      continue;
    }
    for (let i = 0; i < size; i += 1) {
      if (((layer.cells[i] as number) & open) !== 0) {
        opened ??= new Uint8Array(size);
        opened[i] = 1;
      }
    }
  }
  return opened;
}

/**
 * Adds to a map's opened cells those under the passage of every prop that lays one (`passageFootprint`,
 * ADR-017's amendment of 2026-10-05), so the compile and the overlay open the same cells.
 *
 * @param world World, for its tile size.
 * @param map Map document.
 * @param context Compile context, for the rules, the footprints and the frames' sizes.
 * @param opened The cells opened so far at the compiled cell, or null for none.
 * @returns `opened` with the passages marked, allocated with the first prop that has one; null when nothing
 *   opens a cell.
 */
function passageCells(world: WorldDocument, map: MapDocument, context: CompileContext, opened: Uint8Array | null): Uint8Array | null {
  let cells = opened;
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      const footprint = passageFootprint(prop, context, world.tileSize);
      if (footprint === undefined) {
        continue;
      }
      cells ??= new Uint8Array(map.width * map.height * COMPILED_COLLISION_CELLS_PER_TILE * COMPILED_COLLISION_CELLS_PER_TILE);
      stampPassage(cells, map.width, map.height, footprint, prop, world.tileSize, COMPILED_COLLISION_CELLS_PER_TILE, 1, COMPILED_MIN_COVER);
    }
  }
  return cells;
}

/**
 * The cells of a map that placed props open with their footprints' passages (ADR-017's amendment of
 * 2026-10-05), whatever then blocks them: what the collision overlay draws in the open colour where the
 * effective grid is clear, and how the editor finds the prop that opens a cell. A prop with cells of its own
 * opens by its frame's passage, as the compile lays it.
 *
 * @param world World, for its tile size.
 * @param map Map document.
 * @param context Compile context, for the open mask, the footprints and the frames' sizes.
 * @returns One byte per compiled cell, 1 where a prop opens it; null when the game declares no open mask, the
 *   context's build laid no passage, or no placed prop has one.
 */
export function propOpenings(world: WorldDocument, map: MapDocument, context: CompileContext): Uint8Array | null {
  return passageCells(world, map, context, null);
}

/**
 * ORs a grid at tile resolution into one at cell resolution, every tile's byte over all of its cells.
 *
 * @param tiles One byte per tile.
 * @param cells Grid at cell resolution to OR into.
 * @param width Map width in tiles.
 * @param height Map height in tiles.
 * @param perTile Collision cells per tile.
 */
function orTiles(tiles: Uint8Array, cells: Uint8Array, width: number, height: number, perTile: number): void {
  const gridWidth = width * perTile;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const value = tiles[y * width + x] as number;
      if (value === 0) {
        continue;
      }
      for (let cy = 0; cy < perTile; cy += 1) {
        const row = (y * perTile + cy) * gridWidth + x * perTile;
        for (let cx = 0; cx < perTile; cx += 1) {
          cells[row + cx] = (cells[row + cx] as number) | value;
        }
      }
    }
  }
}

/**
 * Applies a corners rule to one terrain layer.
 *
 * @param rule The rule.
 * @param layer The terrain layer.
 * @param width Map width.
 * @param height Map height.
 * @param grid Grid to OR into.
 */
function applyCorners(rule: Extract<CollisionRule, { kind: "corners" }>, layer: TerrainLayer, width: number, height: number, grid: Uint8Array): void {
  const stride = width + 1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const tl = layer.corners[y * stride + x] === rule.color;
      const tr = layer.corners[y * stride + x + 1] === rule.color;
      const bl = layer.corners[(y + 1) * stride + x] === rule.color;
      const br = layer.corners[(y + 1) * stride + x + 1] === rule.color;
      const covered = rule.mode === "interior" ? tl && tr && bl && br : tl || tr || bl || br;
      if (covered) {
        grid[y * width + x] = (grid[y * width + x] as number) | rule.value;
      }
    }
  }
}

/**
 * Applies a shore corner rule at cell resolution: a cell is covered when the bilinear share of the colour
 * at its centre, from the tile's four corners, reaches the threshold.
 *
 * @param rule The rule.
 * @param layer Terrain layer.
 * @param width Map width in tiles.
 * @param height Map height in tiles.
 * @param cells Collision grid at cell resolution to OR into.
 * @param perTile Collision cells per tile.
 */
function applyShore(rule: Extract<CollisionRule, { mode: "shore" }>, layer: TerrainLayer, width: number, height: number, cells: Uint8Array, perTile: number): void {
  const stride = width + 1;
  const gridWidth = width * perTile;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const tl = layer.corners[y * stride + x] === rule.color ? 1 : 0;
      const tr = layer.corners[y * stride + x + 1] === rule.color ? 1 : 0;
      const bl = layer.corners[(y + 1) * stride + x] === rule.color ? 1 : 0;
      const br = layer.corners[(y + 1) * stride + x + 1] === rule.color ? 1 : 0;
      if (tl + tr + bl + br === 0) {
        continue;
      }
      for (let cy = 0; cy < perTile; cy += 1) {
        const v = (cy + 0.5) / perTile;
        for (let cx = 0; cx < perTile; cx += 1) {
          const u = (cx + 0.5) / perTile;
          const share = tl * (1 - u) * (1 - v) + tr * u * (1 - v) + bl * (1 - u) * v + br * u * v;
          if (share >= rule.threshold) {
            const index = (y * perTile + cy) * gridWidth + x * perTile + cx;
            cells[index] = (cells[index] as number) | rule.value;
          }
        }
      }
    }
  }
}

/**
 * Applies a cells rule to one resolved layer.
 *
 * @param value Value to OR in.
 * @param tiles Resolved cells.
 * @param grid Grid to OR into.
 * @param except The layer's corner grid, its width in cells and the colour whose interior cells (all four corners the
 *   colour) the rule leaves out; absent, no cell is left out.
 */
function applyCells(value: number, tiles: Uint32Array, grid: Uint8Array, except?: { readonly corners: Uint8Array; readonly width: number; readonly color: number }): void {
  const stride = except === undefined ? 0 : except.width + 1;
  for (let i = 0; i < tiles.length; i += 1) {
    if (tiles[i] === EMPTY_TILE) {
      continue;
    }
    if (except !== undefined) {
      const x = i % except.width;
      const y = Math.floor(i / except.width);
      const at = y * stride + x;
      if (except.corners[at] === except.color && except.corners[at + 1] === except.color && except.corners[at + stride] === except.color && except.corners[at + stride + 1] === except.color) {
        continue;
      }
    }
    grid[i] = (grid[i] as number) | value;
  }
}

/**
 * The footprints keyed by the packed tile value a tile layer stores without its mirroring (ADR-046, ADR-089),
 * for frames of the default variant whose tileset the world uses. Built once per grid so the per-cell lookup
 * allocates nothing.
 *
 * @param world World, for the tileset index.
 * @param footprints Footprints keyed by frame id.
 * @returns Packed tile to footprint.
 */
function packedFootprints(world: WorldDocument, footprints: Readonly<Record<string, PropFootprint>>): Map<number, PropFootprint> {
  const packed = new Map<number, PropFootprint>();
  for (const [id, footprint] of Object.entries(footprints)) {
    const [tileset, variant, tile] = id.split("/");
    const tilesetIndex = world.tilesets.findIndex((t) => t.id === tileset);
    if (tilesetIndex >= 0 && variant === DEFAULT_VARIANT && tile !== undefined && Number.isInteger(Number(tile))) {
      packed.set(packTile(tilesetIndex, Number(tile)), footprint);
    }
  }
  return packed;
}

/**
 * Applies a tiles rule to manual tile layers and prop layers.
 *
 * @param rule The rule.
 * @param world World, for the tileset index.
 * @param map Map document.
 * @param grid Grid to OR into.
 * @param skipProp Props left to the footprint stamping instead of the anchor marking; a behaviour prop is
 *   always among them, since its collision follows its state at run time.
 * @param skipTile Packed tiles left to the footprint stamping instead of the full-tile fill.
 * @throws Error when a listed prop lies outside the map, where its cell index would alias another row.
 */
function applyTiles(rule: Extract<CollisionRule, { kind: "tiles" }>, world: WorldDocument, map: MapDocument, grid: Uint8Array, skipProp: (prop: PropInstance) => boolean, skipTile: (packed: number) => boolean): void {
  const tilesetIndex = world.tilesets.findIndex((t) => t.id === rule.tileset);
  if (tilesetIndex < 0) {
    return;
  }
  const listed = new Set(rule.tiles);
  for (const layer of map.layers) {
    if (layer.kind === "tile") {
      for (let i = 0; i < layer.tiles.length; i += 1) {
        const packed = layer.tiles[i] as number;
        if (packed !== EMPTY_TILE && !skipTile(packed)) {
          const { tilesetIndex: index, tile } = unpackTile(packed);
          if (index === tilesetIndex && listed.has(tile)) {
            grid[i] = (grid[i] as number) | rule.value;
          }
        }
      }
    } else if (layer.kind === "prop") {
      for (const prop of layer.props) {
        if (prop.frame.tileset === rule.tileset && listed.has(prop.frame.tile) && !skipProp(prop)) {
          if (prop.x < 0 || prop.y < 0 || prop.x >= map.width || prop.y >= map.height) {
            throw new Error(`prop ${prop.id} at (${prop.x}, ${prop.y}) is outside map ${map.id} (${map.width}x${map.height})`);
          }
          const i = Math.floor(prop.y) * map.width + Math.floor(prop.x);
          grid[i] = (grid[i] as number) | rule.value;
        }
      }
    }
  }
}

/**
 * Stamps the footprint of every painted tile that has one (ADR-046), the frame sitting on its cell with the
 * cell's mirroring (ADR-089), as a prop's is: mirrored horizontally or vertically the runs mirror with it
 * (ADR-040 amendment of 2026-10-07).
 *
 * @param cells Collision grid at the compiled cell.
 * @param map Map document.
 * @param tiles The layer's packed tiles.
 * @param footprints Footprints by packed tile without its mirroring.
 * @param tileSize Tile size in pixels.
 * @param value Byte to OR in.
 */
function stampTileFootprints(cells: Uint8Array, map: MapDocument, tiles: Readonly<Uint32Array>, footprints: ReadonlyMap<number, PropFootprint>, tileSize: number, value: number): void {
  const placement = { x: 0, y: 0, flipX: false, flipY: false };
  for (let i = 0; i < tiles.length; i += 1) {
    const packed = tiles[i] as number;
    const footprint = footprints.get(withoutTileFlips(packed));
    if (footprint !== undefined) {
      placement.x = i % map.width;
      placement.y = Math.floor(i / map.width);
      placement.flipX = (packed & TILE_FLIP_X) !== 0;
      placement.flipY = (packed & TILE_FLIP_Y) !== 0;
      stampFootprint(cells, map.width, map.height, footprint, placement, tileSize, COMPILED_COLLISION_CELLS_PER_TILE, value, COMPILED_MIN_COVER);
    }
  }
}

/**
 * The state a behaviour prop is stamped in: the one chosen for its id when its type declares it and it is the
 * initial state or the behaviour gives it a frame, else its type's initial state. A choice whose frame an undo
 * took away falls back rather than refusing, since the overlay redraws before the inspector clears it.
 *
 * @param prop The prop.
 * @param context The compile context, for the object types.
 * @param states Chosen states by prop id.
 * @returns The state id; empty for an unregistered type, which stamps nothing in any state.
 */
function shownState(prop: PropInstance, context: CompileContext, states: ReadonlyMap<string, string> | undefined): string {
  const type = context.objectTypes.find((t) => t.type === prop.behaviour?.type);
  const chosen = states?.get(prop.id);
  if (type === undefined) {
    return "";
  }
  return chosen !== undefined && type.states.some((s) => s.id === chosen) && (chosen === type.initial || prop.behaviour?.frames?.[chosen] !== undefined) ? chosen : type.initial;
}

/**
 * Builds the whole-map collision grid.
 *
 * @param world World document.
 * @param map Map document; props must lie inside the map.
 * @param resolved The map's resolved tile layers.
 * @param context Compile context, for the rules, the outside value and the terrains' colours.
 * @param options `objects: false` leaves behaviour props unstamped (ADR-042, ADR-053): published chunks
 *   carry no thing footprint, since a thing's collision follows its state at run time; the editor's overlay
 *   stamps the initial state, or the state `states` names for the prop's id when its type declares it (ADR-040
 *   amendment of 2026-10-03, P1.6-T74), which the compile never passes, from the anchor the state's frame is
 *   drawn at (`thingAnchor`, ADR-040 amendment of 2026-10-04, P1.6-T83), as the game lays it.
 * @returns `(width * COMPILED_COLLISION_CELLS_PER_TILE) * (height * COMPILED_COLLISION_CELLS_PER_TILE)` bytes,
 *   row-major: the rules over terrain layers (`corners`, `cells`, then shore rules), the opened cells and the
 *   painted layers all computed at the authoring cell (`COLLISION_CELLS_PER_TILE`) and doubled; cleared on every
 *   cell a collision layer opens with the game's open mask and every cell under a plain prop's passage, which
 *   is stamped at the compiled cell; then the `tiles` rules, each filling all the cells of
 *   its tile except tiles whose frame has a footprint, prop and tile footprints (ADR-040, ADR-046), a
 *   plain prop's being its own cells when it carries them (`blockingFootprint`, amendment of 2026-10-04), a
 *   behaviour prop's initial state unless left out, and collision layers without the mask's bits, all ORed in.
 *   A prop's own cells are a footprint like any other: paint that opens a cell opens the terrain rules under it
 *   and never a prop standing there.
 *   No byte carries a bit of the mask.
 * @throws Error when a rule value, the outside value or the open mask is not a byte, a rule value or the
 *   outside value carries a bit of the open mask, a corners rule names a colour its terrain does not define,
 *   a collision layer's size is not the grid's, or a prop a tiles rule covers lies outside the map.
 */
export function buildCollisionGrid(world: WorldDocument, map: MapDocument, resolved: { readonly layers: readonly ResolvedTileLayer[] }, context: CompileContext, options: { readonly objects?: boolean; readonly states?: ReadonlyMap<string, string> } = {}): Uint8Array {
  const rules = context.collision;
  const open = rules.open ?? 0;
  assertByte(rules.outside, "outside");
  assertByte(open, "open");
  assertNotOpen(rules.outside, "outside value", open);
  for (const rule of rules.rules) {
    assertByte(rule.value, "value");
    assertNotOpen(rule.value, "rule value", open);
    if (rule.kind === "corners") {
      const terrain = context.terrains.find((t) => t.id === rule.terrain);
      if (terrain !== undefined && !terrain.colors.some((c) => c.index === rule.color)) {
        throw new Error(`collision rule names colour ${rule.color}, which terrain ${terrain.id} does not define`);
      }
    }
  }
  const footprintRule = rules.rules.find((r): r is Extract<CollisionRule, { kind: "footprints" }> => r.kind === "footprints");
  const footprints = footprintRule === undefined ? {} : (rules.footprints ?? {});
  const footprintOf = (prop: PropInstance): PropFootprint | undefined => (footprintRule === undefined ? undefined : blockingFootprint(prop, context, world.tileSize));
  const tileFootprints = packedFootprints(world, footprints);
  const grid = new Uint8Array(map.width * map.height);
  const placed = new Uint8Array(map.width * map.height);
  for (const rule of rules.rules) {
    if (rule.kind === "footprints") {
      continue;
    }
    if (rule.kind === "tiles") {
      applyTiles(rule, world, map, placed, (prop) => prop.behaviour !== undefined || footprintOf(prop) !== undefined, (packed) => tileFootprints.has(withoutTileFlips(packed)));
      continue;
    }
    for (const layer of map.layers) {
      if (layer.kind !== "terrain" || layer.terrain !== rule.terrain) {
        continue;
      }
      if (rule.kind === "corners" && rule.mode === "shore") {
        continue;
      }
      if (rule.kind === "corners") {
        applyCorners(rule, layer, map.width, map.height, grid);
      } else {
        const tiles = resolved.layers.find((r) => r.id === layer.id);
        if (tiles === undefined) {
          throw new Error(`layer ${layer.id} has no resolved tiles`);
        }
        applyCells(rule.value, tiles.tiles, grid, rule.exceptInterior === undefined ? undefined : { corners: layer.corners, width: map.width, color: rule.exceptInterior });
      }
    }
  }
  const authoring = upsampleGrid(grid, map.width, map.height, COLLISION_CELLS_PER_TILE);
  for (const rule of rules.rules) {
    if (rule.kind !== "corners" || rule.mode !== "shore") {
      continue;
    }
    for (const layer of map.layers) {
      if (layer.kind === "terrain" && layer.terrain === rule.terrain) {
        applyShore(rule, layer, map.width, map.height, authoring, COLLISION_CELLS_PER_TILE);
      }
    }
  }
  for (const layer of map.layers) {
    if (layer.kind === "collision" && layer.cells.length !== authoring.length) {
      throw new Error(`collision layer ${layer.id} has ${layer.cells.length} cells, expected ${authoring.length}`);
    }
  }
  const authoringWidth = map.width * COLLISION_CELLS_PER_TILE;
  const authoringHeight = map.height * COLLISION_CELLS_PER_TILE;
  const cells = upsampleGrid(authoring, authoringWidth, authoringHeight, COMPILED_PER_AUTHORING);
  const painted = openedCells(map, open, authoring.length);
  const opened = passageCells(world, map, context, painted === null ? null : upsampleGrid(painted, authoringWidth, authoringHeight, COMPILED_PER_AUTHORING));
  if (opened !== null) {
    for (let i = 0; i < cells.length; i += 1) {
      if (opened[i] !== 0) {
        cells[i] = 0;
      }
    }
  }
  orTiles(placed, cells, map.width, map.height, COMPILED_COLLISION_CELLS_PER_TILE);
  if (footprintRule !== undefined) {
    for (const layer of map.layers) {
      if (layer.kind === "prop") {
        for (const prop of layer.props) {
          if (prop.behaviour !== undefined) {
            const thing = options.objects === false ? null : stateFootprint(prop, context, world.tileSize, shownState(prop, context, options.states));
            if (thing !== null) {
              const anchor = thingAnchor(prop, context, world.tileSize, thing.w);
              stampFootprint(cells, map.width, map.height, thing, { x: anchor.x, y: anchor.y, flipX: prop.flipX, flipY: prop.flipY }, world.tileSize, COMPILED_COLLISION_CELLS_PER_TILE, footprintRule.value, COMPILED_MIN_COVER);
            }
            continue;
          }
          const footprint = footprintOf(prop);
          if (footprint !== undefined) {
            stampFootprint(cells, map.width, map.height, footprint, prop, world.tileSize, COMPILED_COLLISION_CELLS_PER_TILE, footprintRule.value, COMPILED_MIN_COVER);
          }
        }
      } else if (layer.kind === "tile" && tileFootprints.size > 0) {
        stampTileFootprints(cells, map, layer.tiles, tileFootprints, world.tileSize, footprintRule.value);
      }
    }
  }
  for (const layer of map.layers) {
    if (layer.kind === "collision") {
      const fine = upsampleGrid(layer.cells, authoringWidth, authoringHeight, COMPILED_PER_AUTHORING);
      for (let i = 0; i < cells.length; i += 1) {
        cells[i] = (cells[i] as number) | ((fine[i] as number) & ~open);
      }
    }
  }
  return cells;
}

/**
 * The effective grid at the authoring cell: `buildCollisionGrid` with every authoring cell the OR of the four
 * compiled cells it holds (ADR-113 decision 10), which is what the editor's overlay, the cell inspector and
 * `collisionSourceAt` index by while they work in authoring cells.
 *
 * @param world World document.
 * @param map Map document.
 * @param resolved The map's resolved tile layers.
 * @param context Compile context.
 * @param options As `buildCollisionGrid` takes them.
 * @returns `(width * COLLISION_CELLS_PER_TILE) * (height * COLLISION_CELLS_PER_TILE)` bytes, row-major.
 * @throws Error as `buildCollisionGrid` does.
 */
export function buildAuthoringGrid(world: WorldDocument, map: MapDocument, resolved: { readonly layers: readonly ResolvedTileLayer[] }, context: CompileContext, options: { readonly objects?: boolean; readonly states?: ReadonlyMap<string, string> } = {}): Uint8Array {
  const side = COMPILED_COLLISION_CELLS_PER_TILE;
  return downsampleGrid(buildCollisionGrid(world, map, resolved, context, options), map.width * side, map.height * side, COMPILED_PER_AUTHORING);
}

/**
 * The cells props open, at the authoring cell: `propOpenings` folded as `buildAuthoringGrid` folds the grid.
 *
 * @param world World, for its tile size.
 * @param map Map document.
 * @param context Compile context.
 * @returns One byte per authoring cell; null as `propOpenings` returns it.
 */
export function authoringOpenings(world: WorldDocument, map: MapDocument, context: CompileContext): Uint8Array | null {
  const opened = propOpenings(world, map, context);
  const side = COMPILED_COLLISION_CELLS_PER_TILE;
  return opened === null ? null : downsampleGrid(opened, map.width * side, map.height * side, COMPILED_PER_AUTHORING);
}
